/**
 * @file Labs print/report rendering.
 */

import { appData, state } from "../../app/state/state.js";
import { canAccessView } from "../../app/routing/boardAccess.js";
import {
  showPrintError,
} from "../../shared/reporting/printWindow.js";
import {
  openPrintPreviewModal,
} from "../../shared/reporting/printPreviewModal.js";
import {
  escapeHTML,
} from "../../shared/utils/contentUtils.js";
import {
  formatDate,
  formatDateTime,
} from "../../shared/utils/dateUtils.js";
import {
  renderMarkdownToHtml,
} from "../../shared/utils/markdownRenderer.js";
import {
  getPatientProviderDisplayNames,
} from "../../shared/utils/providerUtils.js";

const INTERNAL_LAB_ID = "internal";
const DEFAULT_INTERNAL_LAB_LABEL = "Internal lab";

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function safeText(value = "") {
  return escapeHTML(String(value ?? ""));
}

function getKnownLabItem(labOrId) {
  if (labOrId && typeof labOrId === "object") {
    return labOrId;
  }

  const labId = String(labOrId || "");
  if (!labId) {return null;}
  const openItems = Array.isArray(appData?.labs) ? appData.labs : [];
  const extraItems = Array.isArray(state?.closedItemsPagination?.labs?.extraItems) ?
    state.closedItemsPagination.labs.extraItems :
    [];
  return [...openItems, ...extraItems].find((item) => item?.id === labId) || null;
}

function getCurrentUserName() {
  return state.currentUserProfile?.name ||
    state.currentUser?.displayName ||
    state.currentUser?.email ||
    "Unknown user";
}

function getInternalLabLabel() {
  return String(appData?.config?.labs?.internalLabLabel || "").trim() ||
    DEFAULT_INTERNAL_LAB_LABEL;
}

function getLabDisplayName(item = {}) {
  if (item?.stream !== "external" || item?.lab === INTERNAL_LAB_ID) {
    return getInternalLabLabel();
  }

  const labId = String(item?.lab || "");
  const externalLab = (appData?.config?.labs?.externalLabs || [])
    .find((entry) => String(entry?.id || "") === labId);
  return externalLab?.name || labId || "Not specified";
}

function getTypeDisplayNames(item = {}) {
  const typeIds = Array.isArray(item?.types) ? item.types.filter(Boolean) : [];
  const typeConfig = Array.isArray(appData?.config?.labTypes) ?
    appData.config.labTypes :
    [];
  return typeIds.map((typeId) => {
    const match = typeConfig.find((entry) => String(entry?.id || "") === String(typeId));
    return match?.name || typeId;
  });
}

function getProviderDisplayNames(item = {}) {
  const providerIds = Array.isArray(item?.provider) ? item.provider : [];
  return getPatientProviderDisplayNames(providerIds).filter(Boolean);
}

function getLogoHtml() {
  const logo = appData?.config?.branding?.logo;
  if (!logo?.url) {return "";}
  return `
    <div class="logo-wrap">
      <img src="${attrValue(logo.url)}" alt="Logo" class="brand-logo">
    </div>
  `;
}

function renderSummaryRow(label, value) {
  return `
    <div class="summary-row">
      <dt>${safeText(label)}</dt>
      <dd>${safeText(value || "Not specified")}</dd>
    </div>
  `;
}

function renderReportHtml(item = {}) {
  const patientName = item?.patient_name || "Untitled patient";
  const title = `Lab sheet - ${patientName}`;
  const providers = getProviderDisplayNames(item).join(", ");
  const typeNames = getTypeDisplayNames(item).join(", ");
  const dueDate = formatDate(item?.due_date) || "Not specified";
  const generatedOn = formatDateTime(new Date());
  const generatedBy = getCurrentUserName();
  const details = item?.details || "";

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${safeText(title)}</title>
      <style>
        @page { size: A4 portrait; margin: 14mm; }
        * { box-sizing: border-box; }
        body {
          margin: 0;
          color: #111827;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          font-size: 11pt;
          line-height: 1.45;
        }
        .sheet {
          max-width: 760px;
          margin: 0 auto;
          padding: 24px 20px 28px;
        }
        .header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 24px;
          border-bottom: 2px solid #111827;
          padding-bottom: 16px;
        }
        h1 {
          margin: 0;
          color: #111827;
          font-size: 20pt;
          font-weight: 750;
          line-height: 1.15;
        }
        .subtitle {
          margin-top: 6px;
          color: #4b5563;
          font-size: 10pt;
        }
        .logo-wrap {
          flex: 0 0 auto;
          max-width: 180px;
          max-height: 72px;
        }
        .brand-logo {
          display: block;
          max-width: 180px;
          max-height: 72px;
          object-fit: contain;
        }
        .summary {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px 16px;
          margin-top: 22px;
          padding: 14px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: #f9fafb;
        }
        .summary-row {
          min-width: 0;
        }
        .summary-row dt {
          margin: 0 0 2px;
          color: #6b7280;
          font-size: 8.5pt;
          font-weight: 700;
          letter-spacing: 0;
          text-transform: uppercase;
        }
        .summary-row dd {
          margin: 0;
          color: #111827;
          font-size: 11pt;
          font-weight: 650;
          overflow-wrap: anywhere;
        }
        .details {
          margin-top: 24px;
        }
        .details h2 {
          margin: 0 0 8px;
          color: #111827;
          font-size: 12pt;
        }
        .details-body {
          min-height: 180px;
          padding: 14px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          white-space: normal;
          overflow-wrap: anywhere;
        }
        .details-body > :first-child { margin-top: 0; }
        .details-body > :last-child { margin-bottom: 0; }
        .details-body p { margin: 0 0 8px; }
        .details-body h1,
        .details-body h2,
        .details-body h3,
        .details-body h4,
        .details-body h5,
        .details-body h6 {
          margin: 12px 0 6px;
          color: #111827;
          font-size: 11pt;
          font-weight: 700;
          line-height: 1.25;
        }
        .details-body ul,
        .details-body ol {
          margin: 6px 0 10px 18px;
          padding-left: 14px;
        }
        .details-body li { margin: 2px 0; }
        .details-body blockquote {
          margin: 8px 0;
          padding: 4px 0 4px 10px;
          border-left: 3px solid #d1d5db;
          color: #4b5563;
        }
        .details-body code {
          border-radius: 3px;
          background: #f3f4f6;
          padding: 1px 3px;
          font-family: ui-monospace, SFMono-Regular, Consolas, "Liberation Mono", monospace;
          font-size: 0.9em;
        }
        .details-body pre {
          margin: 8px 0;
          overflow-wrap: normal;
          white-space: pre-wrap;
        }
        .details-body table {
          width: 100%;
          margin: 10px 0;
          border-collapse: collapse;
        }
        .details-body th,
        .details-body td {
          border-bottom: 1px solid #e5e7eb;
          padding: 4px 6px;
          text-align: left;
          vertical-align: top;
        }
        .footer {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          margin-top: 28px;
          padding-top: 10px;
          border-top: 1px solid #d1d5db;
          color: #6b7280;
          font-size: 8.5pt;
        }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .sheet { max-width: none; padding: 0; }
        }
      </style>
    </head>
    <body>
      <main class="sheet">
        <header class="header">
          <div>
            <h1>${safeText(title)}</h1>
            <div class="subtitle">Lab request summary</div>
          </div>
          ${getLogoHtml()}
        </header>

        <dl class="summary">
          ${renderSummaryRow("Provider", providers)}
          ${renderSummaryRow("Lab", getLabDisplayName(item))}
          ${renderSummaryRow("Work", typeNames)}
          ${renderSummaryRow("Due date", dueDate)}
        </dl>

        <section class="details">
          <h2>Details</h2>
          <div class="details-body">${renderMarkdownToHtml(details, {breaks: true})}</div>
        </section>

        <footer class="footer">
          <div>Generated by ${safeText(generatedBy)}</div>
          <div>Generated on ${safeText(generatedOn)}</div>
        </footer>
      </main>
    </body>
    </html>
  `;
}

/**
 * Returns whether Labs print/report output is currently exposed.
 * @returns {boolean} True when Labs print behavior is implemented.
 */
export function isLabsPrintReportRuntimeEnabled() {
  return true;
}

/**
 * Opens a printable Labs report.
 * @param {string|object} labOrId Labs item id or active Labs item.
 * @returns {void}
 */
export function openLabsPrintReport(labOrId) {
  if (!canAccessView(state.currentUserProfile, "labs")) {
    showPrintError("You do not have access to print Lab sheets.");
    return;
  }

  const item = getKnownLabItem(labOrId);
  if (!item?.id) {
    showPrintError("Lab Item is not available for printing.");
    return;
  }

  openPrintPreviewModal({
    title: `Lab sheet - ${item?.patient_name || "Untitled patient"}`,
    subtitle: "Lab request summary",
    html: renderReportHtml(item),
  });
}
